/** The console frame: a fixed rail on the left, one scrolling main region, and a skip link. */

import { useId, type CSSProperties, type ReactNode } from "react";

import { cn } from "@/lib/cn";

import { lengthOf, mergeStyle } from "../internal/style";
import "../internal/base.css";
import "./AppShell.css";

export interface AppShellProps {
  readonly brand?: ReactNode;
  readonly nav?: ReactNode;
  readonly railFooter?: ReactNode;
  readonly railWidth?: string | number;
  /** Names the rail, for a screen reader. */
  readonly railLabel?: string;
  readonly padding?: "default" | "none";
  readonly className?: string;
  readonly style?: CSSProperties;
  readonly children?: ReactNode;
}

export function AppShell({
  brand,
  nav,
  railFooter,
  railWidth,
  railLabel = "Navigation",
  padding = "default",
  className,
  style,
  children,
}: AppShellProps) {
  const mainId = `${useId()}-main`;
  return (
    <div
      className={cn("al-shell", className)}
      style={mergeStyle(style, { "--al-shell-rail": lengthOf(railWidth) })}
    >
      <a className="al-shell-skip" href={`#${mainId}`}>
        Skip to content
      </a>
      <div className="al-shell-rail">
        {brand !== undefined ? <div className="al-shell-brand">{brand}</div> : null}
        <nav className="al-shell-nav" aria-label={railLabel}>
          {nav}
        </nav>
        {railFooter !== undefined ? <div className="al-shell-railfoot">{railFooter}</div> : null}
      </div>
      <main id={mainId} className={cn("al-shell-main", `al-shell-pad-${padding}`)}>
        {children}
      </main>
    </div>
  );
}
